import React, { useState } from "react";
import { FileDown, Archive, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import { useLanguage } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { toast } from "sonner";

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the click has had time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const filenameFromHeaders = (headers, fallback) => {
  const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  if (!cd) return fallback;
  const m = cd.match(/filename="?([^"]+)"?/i);
  return m ? m[1] : fallback;
};

export const CaseExportButtons = ({ caseId, caseStatus, caseNumber, applicantName }) => {
  const { t } = useLanguage();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const isDraft = caseStatus === "draft";

  const fallbackBase = (() => {
    const padded = caseNumber == null
      ? "case"
      : (caseNumber < 1000 ? `case-${String(caseNumber).padStart(3, "0")}` : `case-${caseNumber}`);
    const slug = (applicantName || "")
      .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return slug ? `${padded}-${slug}` : padded;
  })();

  const onPdf = async () => {
    if (isDraft || pdfBusy) return;
    setPdfBusy(true);
    try {
      const { data, headers } = await api.get(`/cases/${caseId}/pdf`, { responseType: "blob" });
      const filename = filenameFromHeaders(headers, `${fallbackBase}.pdf`);
      triggerBlobDownload(data, filename);
      toast.success(t("toast_pdf_downloaded"));
    } catch (err) {
      const msg = (err?.response?.data instanceof Blob)
        ? await err.response.data.text().catch(() => "")
        : (err?.response?.data?.detail || err?.message || "");
      toast.error(msg || t("err_generic"));
    } finally {
      setPdfBusy(false);
    }
  };

  const onBundle = async () => {
    if (isDraft || zipBusy) return;
    setZipBusy(true);
    try {
      const { data, headers } = await api.get(`/cases/${caseId}/bundle`, { responseType: "blob" });
      const filename = filenameFromHeaders(headers, `${fallbackBase}.zip`);
      triggerBlobDownload(data, filename);
      toast.success(t("toast_bundle_downloaded"));
    } catch (err) {
      const msg = (err?.response?.data instanceof Blob)
        ? await err.response.data.text().catch(() => "")
        : (err?.response?.data?.detail || err?.message || "");
      toast.error(msg || t("err_generic"));
    } finally {
      setZipBusy(false);
    }
  };

  const pdfButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={onPdf}
      disabled={isDraft || pdfBusy}
      className="border-slate-300"
      data-testid="case-download-pdf-button"
      aria-label={t("download_pdf")}
    >
      {pdfBusy ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <FileDown className="h-4 w-4 mr-2" />
      )}
      {t("download_pdf")}
    </Button>
  );

  const bundleButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={onBundle}
      disabled={isDraft || zipBusy}
      className="border-slate-300"
      data-testid="case-download-bundle-button"
      aria-label={t("download_bundle")}
    >
      {zipBusy ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Archive className="h-4 w-4 mr-2" />
      )}
      {t("download_bundle")}
    </Button>
  );

  if (isDraft) {
    return (
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-wrap items-center gap-2" data-testid="case-export-buttons">
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{pdfButton}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("export_disabled_draft")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{bundleButton}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("export_disabled_draft")}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="case-export-buttons">
      {pdfButton}
      {bundleButton}
    </div>
  );
};

export default CaseExportButtons;
